<script setup lang="ts">
import RcMinerSearchCard from '@/components/rc/common/RcMinerSearchCard.vue'
import { searchResources } from '@/services/rc.service'
import type { IResourceSearchFilters } from '@/types/rc/filters'
import type { IMinerIdentifier } from '@/types/rc/miners'
import { useQuery } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { computed, reactive, watch } from 'vue'
import { Icon } from '@iconify/vue'

type PluggableFilters = Omit<Partial<IResourceSearchFilters>, 'resource' | 'fragment'>

const props = withDefaults(
  defineProps<{
    filters?: PluggableFilters
    disabled?: boolean
    placeholder?: string
    multiple?: boolean
    preset?: number | number[]
    exclude?: number[]
  }>(),
  {
    filters: undefined,
    multiple: false,
    disabled: false,
    placeholder: 'Search miners by SN or MAC address...',
    preset: undefined,
    exclude: undefined,
  }
)

const emit = defineEmits(['clear'])

// Model returning array or single object depends on `multiple` prop.
const minersSelected = defineModel<IMinerIdentifier[] | IMinerIdentifier | undefined>({ required: true })
const minersAvailable = computed<OptionType[]>(() => {
  // Always return a valid array of OptionType
  if (sourceData.value) return sourceData.value

  // Fallback when no source data: convert selected miners to OptionType format
  if (!minersSelected.value) {
    return []
  }

  // Handle both single and multiple selection cases
  const selectedArray = Array.isArray(minersSelected.value) ? minersSelected.value : [minersSelected.value]
  return selectedArray
})

const emptyListMessage = computed(() => {
  if (filters.work_order_number) return `No miners found for RWO ${filters.work_order_number}`
  if (filters.customer_id) return `No miners found for customer`
  if (filters.fragment) return 'No miners found'
  return 'Start typing...'
})

// Reactive filters object.
const filters = reactive<IResourceSearchFilters>({
  resource: 'miner_identifier',
  fragment: '',
  ...props.filters,
})

// Update filters when parent component changes them.
watch(
  () => props.filters,
  (newFilters) => {
    Object.assign(filters, newFilters)
  }
)

const { data: sourceData, isPending: minersSearchLoading } = useQuery({
  queryKey: ['searchResources', filters],
  enabled: () => !props.disabled && !!filters.fragment,
  queryFn: () =>
    searchResources(filters)
      .then((miners) =>
        miners
          .filter((miner: IMinerIdentifier) => !props.exclude?.includes(miner.id))
          .map((miner: IMinerIdentifier) => ({
            label: miner.model,
            value: miner,
          }))
      )
      .catch(() => {
        ElMessage.error({ message: `Failed to ${filters.fragment ? 'search' : 'populate'} miners.`, grouping: true })
        return []
      }),
})

// Update
const searchMiners = (value: string) => {
  filters.fragment = value.trim()
}

// Clear
const reset = () => {
  filters.fragment = ''
  minersSelected.value = props.multiple ? [] : undefined
}

defineExpose({ reset })
</script>

<template>
  <el-select-v2
    v-model="minersSelected"
    :options="minersAvailable"
    filterable
    clearable
    remote
    :remote-method="searchMiners"
    :loading="minersSearchLoading"
    :placeholder="placeholder"
    value-key="id"
    :multiple="multiple"
    :disabled="disabled"
    :item-height="96"
    :reserve-keyword="false"
    @clear="emit('clear')"
  >
    <template #label="{ value }">
      <span>{{ value.model }} [{{ value.sn }}]</span>
    </template>
    <template #default="{ item }">
      <RcMinerSearchCard :miner="item.value" />
    </template>
    <template #empty>
      {{ filters.fragment ? emptyListMessage : 'Start typing...' }}
    </template>
    <template v-if="filters.fragment !== ''" #loading>
      <div class="remote-search--loading">
        <Icon icon="eos-icons:three-dots-loading" height="20" /></div
    ></template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
